import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { FiArrowRight, FiAward, FiEdit3, FiExternalLink, FiShield, FiUsers } from "react-icons/fi";
import { MdOutlineSportsCricket } from "react-icons/md";
import ThemedPageFrame from "./ThemedPageFrame";

const SPORTS_BANNERS_API_URL = "https://shop.nabhiverse.com/api/sports-banners";
const SPORTS_API_URL = "https://shop.nabhiverse.com/api/sports";
const SPORTS_ASSET_BASE_URL = "https://yblapis.ganpatigroupbharat.com";
const SPORTS_ADMIN_URL = "https://sports.nabhiverse.com/";
const SPORTS_ROLE_ITEMS = [
    { title: "Admin", icon: FiShield },
    { title: "Partner", icon: FiUsers },
    { title: "Member", icon: FiShield },
    { title: "Captain", icon: FiAward },
    { title: "Scorer", icon: FiEdit3 },
];

const normalizeAssetUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;

    return `${SPORTS_ASSET_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

const normalizeExternalUrl = (url) => {
    if (!url || typeof url !== "string") return "";

    const trimmedUrl = url.trim();
    if (!trimmedUrl || !trimmedUrl.includes(".")) return "";
    if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;

    return `https://${trimmedUrl}`;
};

const Sports = () => {
    const [banners, setBanners] = useState([]);
    const [sports, setSports] = useState([]);
    const [isBannerLoading, setIsBannerLoading] = useState(true);
    const [isSportsLoading, setIsSportsLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchSportsBanners = async () => {
            setIsBannerLoading(true);

            try {
                const response = await axios.get(SPORTS_BANNERS_API_URL);
                const data = response?.data?.data || {};
                const bannerDetails = Array.isArray(data.banner_details) ? data.banner_details : [];
                const yclBanners = Array.isArray(data.ycl_banners) ? data.ycl_banners : [];

                const nextBanners = [
                    ...bannerDetails
                        .map((banner) => ({
                            id: `banner-${banner.BannerId}`,
                            title: banner.Name || "Sports",
                            image: normalizeAssetUrl(banner.ImagePath),
                            link: normalizeExternalUrl(banner.BannerURL),
                        })),
                    ...yclBanners
                        .map((banner) => ({
                            id: `ycl-${banner.BannerId}`,
                            title: banner.Name || "Yuva Cricket League",
                            image: normalizeAssetUrl(banner.webBanner || banner.ImagePath),
                            link: normalizeExternalUrl(banner.BannerURL),
                        })),
                ].filter((banner) => banner.image);

                setBanners(nextBanners);
            } catch (error) {
                console.error("Failed to load sports banners:", error);
                setBanners([]);
            } finally {
                setIsBannerLoading(false);
            }
        };

        const fetchSports = async () => {
            setIsSportsLoading(true);

            try {
                const response = await axios.get(SPORTS_API_URL);
                const nextSports = Array.isArray(response?.data?.data)
                    ? response.data.data.filter((item) => item?.status !== false)
                    : [];

                setSports(nextSports);
            } catch (error) {
                console.error("Failed to load sports:", error);
                setSports([]);
            } finally {
                setIsSportsLoading(false);
            }
        };

        fetchSportsBanners();
        fetchSports();
    }, []);

    const summary = useMemo(
        () => (
            <>
                <span>Sports</span>
                <strong>{isSportsLoading ? "--" : sports.length}</strong>
            </>
        ),
        [isSportsLoading, sports.length]
    );

    return (
        <ThemedPageFrame
            activePath="/sports"
            eyebrow="Nabhiverse Sports"
            title="Sports"
            subtitle="Explore sports leagues, competitions, and youth talent platforms from Nabhiverse partners."
            summary={summary}
            className="sports_page_layout"
        >
            <div className="sports_page_shell">
                <section className="desktop_hero_section sports_banner_section" aria-label="Sports banners">
                    {isBannerLoading ? (
                        <div className="sports_banner_skeleton"></div>
                    ) : banners.length ? (
                        <Swiper
                            className="desktop_hero_swiper sports_banner_swiper"
                            modules={[Autoplay, Pagination]}
                            pagination={{ clickable: true }}
                            loop={banners.length > 1}
                            autoplay={banners.length > 1 ? { delay: 3500, disableOnInteraction: false } : false}
                        >
                            {banners.map((banner) => (
                                <SwiperSlide key={banner.id}>
                                    <div className="desktop_hero has_image sports_banner_slide">
                                        <img className="desktop_hero_img" src={banner.image} alt={banner.title} />
                                        {banner.link ? (
                                            <a
                                                className="desktop_hero_banner_link"
                                                href={banner.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Open ${banner.title}`}
                                            />
                                        ) : null}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="sports_banner_empty">
                            <MdOutlineSportsCricket />
                            <span>Sports banners coming soon</span>
                        </div>
                    )}
                </section>

                <section className="sports_section">
                    <div className="events_page_section_head sports_section_head">
                        <div>
                            <h2>Sports</h2>
                            <p>Choose a league and continue to the official sports experience.</p>
                        </div>
                        <span>{isSportsLoading ? "--" : sports.length} Sports</span>
                    </div>

                    {isSportsLoading ? (
                        <div className="sports_grid">
                            {[1, 2].map((item) => (
                                <div key={item} className="sports_card sports_card_skeleton">
                                    <span></span>
                                    <div></div>
                                    <p></p>
                                </div>
                            ))}
                        </div>
                    ) : sports.length ? (
                        <div className="sports_grid">
                            {sports.map((item, index) => (
                                <article key={item.id || item.title || index} className="sports_card">
                                    <a
                                        className="sports_card_visual"
                                        href={item.link || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Visit ${item.title}`}
                                    >
                                        <div className="sports_card_logo">
                                            {item.logo ? (
                                                <img src={item.logo} alt={`${item.title} logo`} />
                                            ) : (
                                                <MdOutlineSportsCricket />
                                            )}
                                        </div>
                                    </a>
                                    <div className="sports_card_content">
                                        <strong>{item.title}</strong>
                                        <p>{item.description}</p>
                                    </div>
                                    <a
                                        className="sports_visit_button"
                                        href={item.link || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span>Visit Now</span>
                                        <FiArrowRight />
                                    </a>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="events_page_empty">
                            <FiExternalLink />
                            <h2>No sports available</h2>
                            <p>Please check back later.</p>
                        </div>
                    )}
                </section>

                <section className="sports_roles_section">
                    <div className="events_page_section_head sports_section_head">
                        <div>
                            <h2>Sports Access</h2>
                            <p>Continue with the role that fits your sports journey.</p>
                        </div>
                        <span>{SPORTS_ROLE_ITEMS.length} Options</span>
                    </div>

                    <div className="sports_roles_grid">
                        {SPORTS_ROLE_ITEMS.map((item) => {
                            const Icon = item.icon;

                            return (
                                <button
                                    key={item.title}
                                    className="sports_role_card"
                                    type="button"
                                    onClick={() => {
                                        window.location.href = SPORTS_ADMIN_URL;
                                    }}
                                >
                                    <span className="sports_role_icon">
                                        <Icon />
                                    </span>
                                    <strong>{item.title}</strong>
                                    <small>Explore</small>
                                </button>
                            );
                        })}
                    </div>
                </section>
            </div>
        </ThemedPageFrame>
    );
};

export default Sports;
